/**
 * LaTeX math delimiter preprocessing for the Markdown renderer.
 *
 * The agent emits real mathematics using LaTeX delimiters:
 *   - inline:  \( ... \)
 *   - display: \[ ... \]
 * and NEVER uses single `$` (which collides with prices like "$88.88/mo" and
 * with anything users type). `remark-math`, however, only understands `$`/`$$`.
 * So we bridge the two: convert the backslash delimiters to `$`/`$$` and feed
 * the battle-tested `remark-math` + `rehype-katex` pipeline.
 *
 * WHY A PRE-PARSE STRING TRANSFORM (not a remark plugin like remarkSpoiler):
 * CommonMark treats `\(`, `\)`, `\[`, `\]` as backslash-escapes of ASCII
 * punctuation and STRIPS the backslash during parsing. By the time any remark
 * plugin runs on the mdast, `\(x\)` has already become the text `(x)` — the
 * delimiter signal is gone, and matching bare `(...)`/`[...]` would turn every
 * prose parenthetical into math. The conversion therefore has to happen on the
 * raw string BEFORE markdown is parsed. (A custom micromark syntax extension
 * could also recognize the delimiters pre-escape, but that is far heavier than
 * this transform for a 2-delimiter conversion.)
 *
 * SAFETY PROPERTIES (all covered by mathDelimiters.test.ts):
 *  - Prices/`$` are neutralized (`$` → `\$`, IDEMPOTENTLY — an already-escaped
 *    `\$` is left alone), so "$88.88 ... $799.99" can never be parsed as a
 *    `$...$` math span. remark-math renders `\$` as a literal `$`, so the
 *    displayed text is unchanged. Two unescaped `$` can never survive to pair,
 *    so no prose run becomes a spurious equation.
 *  - Code is preserved: fenced blocks and inline code spans are split out and
 *    left untouched, so `$`-escaping and delimiter conversion never corrupt
 *    `echo $HOME` or a `\(group\)` regex shown as code.
 *  - Autolink URLs are preserved: a bare `http(s)://…`/`www.…` run is split out
 *    too, so a `$` inside a URL is not escaped (which would corrupt the link).
 *  - Display math forces a block: `\[...\]` → blank-line-wrapped `$$` so the
 *    fences sit alone on their lines (the only form remark-math renders in
 *    display mode), even when the equation is written mid-sentence.
 *  - Spoilers are preserved: delimiter conversion is skipped inside `||…||` so
 *    introduced math can't split the spoiler's text node and leak the `||` +
 *    hidden content (math just doesn't typeset inside a spoiler).
 *
 * KNOWN LIMITATIONS (both rare, both fail safe — no crash, no security issue):
 *  1. remark-math's INLINE tokenizer cannot carry a literal `$` between `$...$`
 *     — even an escaped `\$` closes the span. So an inline `\( … \$ … \)` (a
 *     LaTeX-escaped dollar INSIDE inline math) cannot render and degrades to a
 *     KaTeX error span (no text leak). Display `\[ … \$ … \]` is fine. The agent
 *     prompt reserves `$` for currency and keeps it OUTSIDE math.
 *  2. CommonMark 4-space-INDENTED code blocks are not protected (only fenced
 *     ``` / ~~~ ` and inline `…` code are). A `$` or `\(…\)` on a 4-space-
 *     indented line is escaped/converted. A correct fix needs line-aware block
 *     parsing (a naive indent guard would wrongly protect list-continuation
 *     lines and break math inside nested lists). The agent emits fenced code,
 *     so this is not expected in practice; documented rather than fixed.
 */

// Splits a string into alternating [prose, protected, prose, …] segments.
// Captured (odd-index) segments pass through UNCHANGED:
//  - fenced code blocks (``` / ~~~) and inline code spans (`…`, ``…``)
//  - bare autolink URLs (http(s)://… / www.…) up to the next space or `<`
// so neither `$`-escaping nor delimiter conversion ever touches their content.
const PROTECTED_SEGMENT =
  /(```[\s\S]*?```|~~~[\s\S]*?~~~|`+[^`\n]*?`+|(?:https?:\/\/|www\.)[^\s<]+)/g;

// Display math \[ ... \]  →  $$ ... $$   (content may span multiple lines)
const DISPLAY_MATH = /\\\[([\s\S]+?)\\\]/g;
// Inline math  \( ... \)  →  $ ... $
const INLINE_MATH = /\\\(([\s\S]+?)\\\)/g;
// `\(` `\)` `\[` `\]` delimiters NESTED inside a display block. They are invalid
// in KaTeX math mode, so unwrap them to bare content (`\[\text{x}\(n\)\]` → the
// inner `\(n\)` becomes `n`). NOTE: only matches a backslash immediately before
// one of ( ) [ ] — multi-letter commands like `\left(` (the `(` follows `t`,
// not `\`) and `\{` are untouched.
const NESTED_DELIM = /\\[()[\]]/g;
// A single `$` not already backslash-escaped. Capture-group form (NOT a
// lookbehind) for Safari < 16.4 compatibility — mirrors remarkMention.ts.
const UNESCAPED_DOLLAR = /(^|[^\\])\$/g;
// `||spoiler||` spans — mirrors remarkSpoiler's own `/\|\|(.+?)\|\|/`. Delimiter
// CONVERSION is skipped inside them (see transformProse). `$`-escaping is NOT
// skipped, so a user's `$5` inside a spoiler still can't become math.
const SPOILER = /(\|\|[^\n]*?\|\|)/g;

// Convert the LaTeX delimiters to remark-math `$`/`$$` syntax. Runs only on
// NON-spoiler prose (code/URLs are already carved out by PROTECTED_SEGMENT).
function convertMathDelimiters(text: string): string {
  // Display first ($$ is longer; convert before inline so the inline rule never
  // sees a `\[`'s bracket). remark-math renders `$$` in DISPLAY mode (centered,
  // large, limits above/below) only as FLOW math — when each `$$` fence sits
  // ALONE on its own line, like a fenced code block. `$$x$$` on one line renders
  // inline, and `text $$\nx\n$$ text` (a fence sharing a line with prose) breaks
  // parsing entirely (the equation leaks as literal text / an empty block). So
  // wrap with BLANK lines to force the fences onto their own lines regardless of
  // surrounding text: `\[...\]` is inherently a display (block) construct. INNER
  // is trimmed so a delimiter the agent already put on its own line doesn't gain
  // a stray blank line; react-markdown collapses any redundant blank lines.
  // Nested inline delimiters inside INNER are unwrapped (they would otherwise be
  // rewritten to `$…$` by the inline pass and crash KaTeX inside the `$$`).
  let out = text.replace(
    DISPLAY_MATH,
    (_m, inner: string) =>
      `\n\n$$\n${inner.replace(NESTED_DELIM, "").trim()}\n$$\n\n`,
  );
  // Inline math (any \[...\] is already a $$ block, so this only sees true
  // inline spans).
  out = out.replace(INLINE_MATH, (_m, inner: string) => `$${inner}$`);
  return out;
}

function transformProse(segment: string, convert: boolean): string {
  // 1. Neutralize every UN-escaped `$` so prices/user text never parse as math.
  //    Idempotent: an existing `\$` is left intact (no double-escaping). Because
  //    each escaped `$` gains a leading `\`, two prose `$` can never both stay
  //    unescaped, so they can never pair into a spurious `$…$` math span. Done
  //    on the WHOLE segment, spoilers included, so a `$5` inside `||…||` is safe.
  //    Always runs (even when delimiter conversion is off) so a legacy "$5…$9"
  //    message can never become spurious math.
  const escaped = segment.replace(
    UNESCAPED_DOLLAR,
    (_m, pre: string) => `${pre}\\$`,
  );
  // When delimiter conversion is disabled (legacy HTML-sourced content — see
  // preprocessMath), stop here: those `\[`/`\(` are Turndown bracket-escapes,
  // NOT the agent's LaTeX, and must not be turned into math.
  if (!convert) {
    return escaped;
  }
  // 2. Convert `\(…\)`/`\[…\]` — but NOT inside `||spoiler||` spans. remark-math
  //    is a parse-time tokenizer that runs BEFORE remarkSpoiler's transformer;
  //    if we introduced `$x$` inside a spoiler it would tokenize and split the
  //    text node, so remarkSpoiler could no longer match across it — leaking the
  //    `||` delimiters AND the supposedly-hidden content. Leaving the delimiters
  //    unconverted inside a spoiler keeps the spoiler intact (the `\(x\)` just
  //    renders as literal `(x)` once revealed — math doesn't typeset inside a
  //    spoiler, an acceptable, non-leaking trade-off).
  return escaped
    .split(SPOILER)
    .map((part, i) => (i % 2 === 1 ? part : convertMathDelimiters(part)))
    .join("");
}

/**
 * Convert the agent's LaTeX delimiters to `remark-math` syntax, code-aware.
 *
 * @param source  Raw message text (native markdown, OR the output of
 *   `htmlToMarkdown` for legacy HTML messages).
 * @param convertDelimiters  Whether to convert `\(…\)`/`\[…\]` into `$`/`$$`.
 *   Pass `true` (default) for native-markdown content, where these delimiters
 *   are the agent's real LaTeX. Pass `false` for legacy HTML-sourced content:
 *   `htmlToMarkdown` (Turndown) backslash-escapes every literal bracket
 *   (`[` → `\[`, `]` → `\]`), which is INDISTINGUISHABLE from display-math
 *   delimiters — so converting them would render plain text like
 *   `[AutoErrorReport]` or a JSON array `[{…}]` as centered KaTeX. Legacy HTML
 *   messages predate the agent's LaTeX feature and never contain real math, so
 *   skipping conversion is lossless. `$` neutralization still runs either way.
 *
 * Safe to run on every message: text without math delimiters (or with
 * conversion off and no `$`) is returned unchanged.
 */
export default function preprocessMath(
  source: string,
  convertDelimiters = true,
): string {
  if (!source) {
    return source;
  }
  const hasDollar = source.includes("$");
  const hasDelimiters = source.includes("\\(") || source.includes("\\[");
  // Fast path: nothing to neutralize (`$`) and — when converting — no
  // delimiters to convert. The common case for plain chat messages. When
  // conversion is off, delimiters are irrelevant, so only `$` forces work.
  if (!hasDollar && (!convertDelimiters || !hasDelimiters)) {
    return source;
  }
  return source
    .split(PROTECTED_SEGMENT)
    .map((seg, i) =>
      i % 2 === 1 ? seg : transformProse(seg, convertDelimiters),
    )
    .join("");
}
